/*eslint react-hooks/exhaustive-deps: "off"*/
import React, {useEffect} from 'react';
import {Tag} from "antd";
import MapBase from "./MapBase";
import useMapProps from "./useMapProps";
import {MapPropsProvider} from "./MapPropsContext";
import WidgetTableClicked from "./widgets/WidgetTableClicked";
import WidgetContainer from "./widgets/WidgetContainer";
import {getApiUrl, makeTableSorterFn} from "../../utils";
import WidgetLegend from "./widgets/WidgetLegend";
import WidgetTableBroadcast from "./widgets/WidgetTableBroadcast";
import WidgetDistrictSelect from "./widgets/WidgetDistrictSelect";
import WidgetHeatmapSwitch from "./widgets/WidgetHeatmapSwitch";
import WidgetToolbar from "./widgets/WidgetToolbar";
import {WELL_KNOWN_POSITION} from "../../Constants";
import WidgetDmbDtv from "./widgets/WidgetDmbDtv";
import AlignCenterText from "./widgets/AlignCenterText";


const ICON_MAPPING = {
  'DMB': ['dmb', 'DMB'],
  'DTV': ['dtv', 'DTV'],
  // '양호': ['marker-1', '양호'],  // 없는 아이콘
  // '보통': ['marker-2', '보통'],
  // '불량': ['marker-3', '불량'],
};

const DMBDTV_CODE_OPTIONS = Object.entries(ICON_MAPPING).map(([dmbDtvCode, [iconCode, iconLabel]]) => {
  return { brc_knd: dmbDtvCode, label: iconLabel };
});

const ICON_CODE_BY_DMBDTV_CODE = Object.entries(ICON_MAPPING).reduce((acc, [dmbDtvCode, [iconCode]]) => {
  acc[dmbDtvCode] = iconCode;
  return acc;
}, {});



const MapRadioRegional = () => {
  const mapProps = useMapProps({
    INITIAL_VIEW_STATE: {
      ...WELL_KNOWN_POSITION.세종특별자치시,
      zoom: 8,
    },
    INITIAL_POINT_LAYER_PROPS: {
      getIconCode: ({brc_knd}) => {
        try {
          return ICON_MAPPING[brc_knd][0]
        } catch (e) {
          console.error("not found iconCode for", brc_knd);
          return null;
        }

      },
      // getIconCode: ({ rcv_grad }) => ICON_MAPPING[rcv_grad][0],
      latitudeExtractorForHeatmap: ({position}) => position[1],
      longitudeExtractorForHeatmap: ({position}) => position[0],
      intensityExtractorForHeatmap: ({rcv_grad}) => ({'양호': 1, '보통': 0, '불량': -1}[rcv_grad]),
    },
    INITIAL_WIDGET_STATE: {
      dmb_dtv_code_list: Object.keys(ICON_MAPPING),
    }
  });

  const {
    clickedPointIconObjects,
    viewState,
    currentDistrictCode,
    setGeojsonExtraParams,
    setIsHeapmapByPoint,
    pointLayerProps,
    setPointsLayerFilterFunc,
    widgetState,
  } = mapProps;

  const {latitude, longitude} = viewState;

  useEffect(() => {
    setIsHeapmapByPoint(true);
  }, []);

  useEffect(() => {
    setGeojsonExtraParams(prev => ({
      ...prev,
      currentDistrictCode,
    }));
  }, [currentDistrictCode]);

  window.pointLayerProps = pointLayerProps;
  useEffect(() => {
    const {dmb_dtv_code_list} = widgetState;
    setPointsLayerFilterFunc(() => ({brc_knd}) => dmb_dtv_code_list.includes(brc_knd));
  }, [widgetState]);

  return (
    <MapPropsProvider value={mapProps}>
      <MapBase>
        <WidgetContainer>
          <div>
            <WidgetDistrictSelect/>
            <WidgetHeatmapSwitch/>
            <WidgetDmbDtv options={DMBDTV_CODE_OPTIONS} iconCodeByBrcCode={ICON_CODE_BY_DMBDTV_CODE}/>
          </div>
        </WidgetContainer>

        <WidgetTableClicked
          fetchUrl={getApiUrl({type: "type2"})}
          getTitle={objects => `${objects.length}개의 방송수신정보`}
          objects={clickedPointIconObjects}
          getRowTitle={({brds_nm, brc_knd}) => `${brds_nm} (${brc_knd})`}
          getRow={({uid, brds_nm, brc_knd, eltfield_is, rcv_grad, frq_hz}) =>
            <table>
              <tbody>
              <tr>
                <th>방송사</th>
                <th>방송종류</th>
                <th>전계강도</th>
                <th>측정상태</th>
                <th>주파수</th>
              </tr>
              <tr>
                <td>{brds_nm}</td>
                <td>{brc_knd}</td>
                <td className="number">{eltfield_is}</td>
                <td>{formatRcvGrad(rcv_grad)}</td>
                <td className="number">{frq_hz}</td>
              </tr>
              </tbody>
            </table>
          }
        />

        <WidgetTableBroadcast
          latitude={latitude}
          longitude={longitude}
          fetchUrl={getApiUrl({type: "type1"})}
          getTitle={(layer) => `${layer.length}개의 방송 수신정보`}
          columns={[
            {title: '순번', dataIndex: 'index', key: 'index', width: 75, fixed: 'left', align: 'center', sorter: makeTableSorterFn('index')},
            {title: '방송종류', dataIndex: 'brc_knd', key: 'brc_knd', width: 100, fixed: 'left'
              , sorter: makeTableSorterFn('brc_knd')},
            {title: '방송사', dataIndex: 'brds_nm', key: 'brds_nm', width: 80,
              sorter: makeTableSorterFn('brds_nm')},
            {title: '측정량', dataIndex: 'eltfield_is', key: 'eltfield_is', width: 100,
              sorter: makeTableSorterFn('eltfield_is')},
            {title: '측정상태', dataIndex: 'rcv_grad', key: 'rcv_grad', render: formatRcvGrad, width: 100
              , sorter: makeTableSorterFn('rcv_grad')
            },
            {title: '측정일자', dataIndex: 'mesure_de', key: 'mesure_de', width: 150,
              render: text => {
                return `${text.slice(0, 4)}년 ${text.slice(4, 6)}월 ${text.slice(6, 8)}일`;
              },
              sorter: makeTableSorterFn('mesure_de')},
            {title: '주파수', dataIndex: 'frq_hz', key: 'frq_hz', width: 100,
              sorter: makeTableSorterFn('frq_hz')},
            {title: '주소', dataIndex: 'mesure_spot', key: 'mesure_spot',
              sorter: makeTableSorterFn('mesure_spot')},
            {title: '경도', dataIndex: 'lon', key: 'lon', width: 140,
              render: text => AlignCenterText({ text })
            },
            {title: '위도', dataIndex: 'lat', key: 'lat', width: 140,
              render: text => AlignCenterText({ text })
            },
          ]}
        />

        <WidgetLegend />
      </MapBase>
      <WidgetToolbar/>
    </MapPropsProvider>
  );
};


const formatRcvGrad = (rcv_grad) => {
  // 양호/보통/불량
  const getColor = () => {
    if (rcv_grad === '양호') return 'green';
    else if (rcv_grad === '보통') return 'blue';
    else return 'red';
  };

  return <Tag color={getColor()}>{rcv_grad}</Tag>;
};


export default MapRadioRegional;

